// Recovery from the "Loading chunk N failed" class of error, which is caused by
// a deploy rather than by anything the user did.
//
// The mechanism, because it is not obvious: when a newly deployed service worker
// takes control of a page that is *already open* and still running the previous
// build's JavaScript, that page is left holding the old webpack chunk manifest.
// The next time the user opens a lazily-loaded route (src/components/index.js
// loads every page through React.lazy), webpack asks for a chunk filename that
// the deploy has already replaced, the request 404s, and the import rejects.
//
// There is no error boundary in this app, so that rejection unmounts the whole
// React tree: the user gets a blank page, not a broken route. A hard reload fixes
// it because it fetches the new shell — but a child on an iPad will not know to do
// that, so the app has to do it for them.
//
// This does not *prevent* the mismatch; it recovers from it. Prevention is now
// mostly handled elsewhere and this is the net rather than the front line:
// sw-custom.js does not call skipWaiting() at the top level, so a new worker
// waits instead of seizing an open page, and UpdateAvailableBanner asks before
// promoting it. That used to be untenable because dropping skipWaiting traded
// this failure for stale content -- until utils/serviceWorkerUpdates.js gave a
// long-lived session a way to find out about a deploy at all. What is left for
// this to catch is the user who taps Reload on one tab while another is open,
// and anything that gets a worker activated without going through the banner.
//
// One such thing now exists deliberately, added 27 September 2026: sw-custom.js
// promotes itself for a client that does not answer its capability query,
// because a build predating UpdateAvailableBanner can never promote a worker at
// all and was stuck on that build forever. Those clients are moved with a full
// navigation rather than left on a page whose chunk manifest is now wrong, so
// they do not arrive here. A sibling tab on a CURRENT build can, exactly as it
// can when someone taps Reload in the other tab, which is the case the paragraph
// above already covered.

const RELOAD_STATE_KEY = "wm:stale-build-reloads"

// A reload that does not fix the problem must not become a reload loop, so this
// is a hard cap on attempts rather than a debounce. A debounce only spaces the
// reloads out -- a failure cycle slower than the interval still refreshes
// forever, which on a slow connection is the likely case, not the unlikely one.
const MAX_ATTEMPTS = 2

// ...but a genuinely new deploy much later in the same session deserves a fresh
// allowance, otherwise a long-lived tab can only ever recover once.
const ATTEMPT_RESET_MS = 30 * 60 * 1000

// webpack's own wording, both flavours. ChunkLoadError is what newer webpack
// sets as the error name; older builds only carry the message.
const CHUNK_ERROR_MESSAGE = /Loading (?:CSS )?chunk \S+ failed/i

// The same failure wearing a completely different error, and the reason this
// module could sit installed and correct for weeks while still letting a blank
// page through. Observed on staging, 26 September 2026.
//
// Everything above assumes a missing chunk produces a 404, because that is what
// makes webpack's script onerror fire and say "Loading chunk N failed". The
// server did not do that. `serve -s` rewrites every NOT-FOUND request to
// index.html, `/static/js/*` included, so a deleted chunk came back **200 with
// text/html**. The browser parsed `<!doctype html>` as JavaScript and threw
// `SyntaxError: Unexpected token '<'`, which is not a chunk error by name or by
// message, so isStaleBuildError said no and nothing recovered. Blank page.
//
// The Procfile and serve.json now make those requests 404, so the path above is
// the one that should fire. This stays as the net beneath it, because the same
// shape can arrive from anywhere that answers a script request with a page: a
// Cloudflare error or challenge page, a captive portal, or a future change to the
// hosting config by somebody who has never read this file.
//
// Matched narrowly on purpose. "Unexpected token '<'" means the response was
// markup, and a real syntax error in our own minified bundle cannot reach
// production because the build would have failed first. The filename check keeps
// it to our own scripts, and MAX_ATTEMPTS still caps this at two reloads, so the
// worst case if it ever misfires is the blank page we already had, two reloads
// later.
// Two parts rather than one regex, and NOT written as /Unexpected token '?</ even
// though that is the obvious spelling. A lone apostrophe in this file breaks
// src/ownerCopy.test.js: its comment stripper tracks quotes so that a // inside a
// string does not start a comment, so one unpaired quote in CODE leaves it
// believing everything after is a string, and every comment below stops being
// stripped. It then reports a double hyphen in a comment somewhere else entirely,
// which is a genuinely baffling failure to be handed. Keep the quote out.
const UNEXPECTED_TOKEN = /Unexpected token/i

const MARKUP_OPENER = "<"

// Deliberately the /static/js/ path and not "anything ending .js". Every script
// this build emits lives under that path. The looser rule would also have matched
// the Trustpilot widget, which is loaded on every page from widget.trustpilot.com
// and ends in .min.js: a third-party script whose parse error would then have
// reloaded the whole app, which is the exact loop MAX_ATTEMPTS exists to bound
// rather than something to go looking for.
const OUR_SCRIPT_URL = /\/static\/js\//i

// Scripts AND stylesheets, because a missing CSS chunk fails the same way and
// from the same cause. Same /static/ restriction and the same reason for it.
const OUR_ASSET_URL = /\/static\/(js|css)\//i

const safeStorage = win => {
	// Private-mode Safari and blocked-cookie settings make this throw on access,
	// not just on write.
	try {
		return win.sessionStorage
	} catch (e) {
		return null
	}
}

export const isStaleBuildError = error => {
	if (!error) return false
	if (error.name === "ChunkLoadError") return true

	const message = typeof error === "string" ? error : error.message

	return typeof message === "string" && CHUNK_ERROR_MESSAGE.test(message)
}

/**
 * The third shape: one of the shell's own <script> or <link> tags failed to
 * load at all.
 *
 * This is the case a correct server creates, which is worth saying plainly
 * because it sounds backwards. While missing assets were answered 200 with
 * index.html, a stale shell threw a SyntaxError and isHtmlServedAsScript caught
 * it. Once public/serve.json made a missing file 404 honestly, that SyntaxError
 * stopped happening and this took its place -- and nothing was watching for it,
 * so the SAME blank page came back with a cleaner cause. Measured on staging,
 * 26 September 2026: two "Failed to load resource: 404" lines, no error anything
 * else could recognise, and no reload.
 *
 * It needs its own handler because a resource load failure is nothing like a
 * thrown error:
 *
 *   - The event carries NO `message` and NO `error`, so both checks above see
 *     undefined and say no. The only evidence is `event.target`.
 *   - It does NOT bubble. A listener on window in the normal phase never runs,
 *     which is why installStaleBuildRecovery registers this one with capture.
 *
 * Restricted to our own /static/ so a third-party tag that fails (the Trustpilot
 * widget, an ad blocker eating something) cannot reload the app underneath
 * somebody, and to SCRIPT and LINK because those are the two that decide whether
 * the shell can run at all. An <img> that 404s is a cosmetic problem and must
 * not cost a reload.
 */
export const isOwnAssetThatFailedToLoad = event => {
	if (!event) return false

	const target = event.target

	if (!target || target === window) return false

	const tag = target.tagName

	if (tag !== "SCRIPT" && tag !== "LINK") return false

	// A <link> that is not a stylesheet (preload, manifest, icon) is not what
	// stops the app booting.
	if (tag === "LINK" && target.rel !== "stylesheet") return false

	const url = tag === "SCRIPT" ? target.src : target.href

	return typeof url === "string" && OUR_ASSET_URL.test(url)
}

/**
 * The second shape: one of our own scripts came back as markup, so the browser
 * threw a SyntaxError while parsing it rather than a chunk error while fetching
 * it. Needs the error event, not just the error, because the only thing that
 * distinguishes "a page was served for my script" from "somebody shipped broken
 * JavaScript" is which file it happened in.
 */
export const isHtmlServedAsScript = event => {
	if (!event) return false

	const message = typeof event.message === "string" ? event.message : ""
	const name = event.error && event.error.name

	// A SyntaxError is the only thing parsing markup can raise here. Checking the
	// name as well as the wording keeps an unrelated error that happens to mention
	// a "<" out of it.
	if (name && name !== "SyntaxError") return false
	if (!UNEXPECTED_TOKEN.test(message) || !message.includes(MARKUP_OPENER)) return false

	// No filename means we cannot tell whose script it was, and reloading on an
	// anonymous syntax error is how a third-party widget takes the whole app into
	// a refresh loop.
	return typeof event.filename === "string" && OUR_SCRIPT_URL.test(event.filename)
}

// How long to wait for the promoted worker to take control before reloading
// anyway. Matches UpdateAvailableBanner's own fallback: the reload is the point,
// and a worker that never answers must not leave the user on a blank page.
const PROMOTE_FALLBACK_MS = 2000

/**
 * Reload, but promote a waiting service worker first when there is one.
 *
 * A plain reload is not enough and that is the whole reason this exists. The
 * failure being recovered from is a shell the service worker is serving from
 * its precache, so reloading fetches THE SAME STALE SHELL, fails the same way,
 * and burns an attempt. Measured on staging 26 September 2026: the live
 * precache held an index.html from one build and the main chunk from another,
 * so every reload asked for a file nothing had.
 *
 * When a new worker is waiting, promoting it is what actually changes the
 * answer. This is deliberately the same sequence UpdateAvailableBanner uses for
 * the button, including waiting for `controllerchange` rather than reloading on
 * the next line, because postMessage is asynchronous and reloading immediately
 * races the activation and can land straight back on the old build.
 *
 * Everything degrades to a plain reload: no service worker support, no
 * registration, none waiting, or a worker that never takes control within
 * PROMOTE_FALLBACK_MS.
 *
 * SUPERSEDED IN PART, 26 September 2026. Kept above rather than rewritten,
 * because it is exactly why this function exists and it was true when written.
 * sw-custom.js now answers navigations from the NETWORK rather than from the
 * precached shell, so "reloading fetches THE SAME STALE SHELL" is no longer the
 * default outcome: an online reload lands on the shell the server has. The
 * promotion below is therefore belt rather than the load-bearing part. It still
 * earns its place for a reload made while offline, where the cached shell is all
 * there is, and for getting an already-installed worker into service in one step
 * instead of waiting for every tab to close.
 */
const promoteWaitingWorkerThenReload = win => {
	const reload = () => win.location.reload()
	const container = win.navigator && win.navigator.serviceWorker

	if (!container || typeof container.getRegistration !== "function") {
		reload()

		return
	}

	let done = false
	const go = () => {
		if (done) return
		done = true
		reload()
	}

	if (typeof win.setTimeout === "function") win.setTimeout(go, PROMOTE_FALLBACK_MS)

	Promise.resolve(container.getRegistration())
		.then(registration => {
			const waiting = registration && registration.waiting

			if (!waiting) {
				go()

				return
			}

			container.addEventListener("controllerchange", go)
			waiting.postMessage({ type: "SKIP_WAITING" })
		})
		.catch(go)
}

/**
 * Reload once to pick up the newly deployed shell. Returns whether it reloaded,
 * so the caller (and the tests) can tell recovery from give-up.
 */
export const recoverFromStaleBuild = (win = window, now = Date.now()) => {
	// Offline is the other thing that produces this error: webpack reports a
	// chunk it could not fetch the same way whether the file is gone or the
	// network is. Reloading an offline page throws away whatever the user was
	// doing and cannot possibly fix it, so leave it to the offline handling.
	if (win.navigator && win.navigator.onLine === false) return false

	const storage = safeStorage(win)

	// No storage means no memory across a reload, and no memory means nothing
	// can bound the reloads. A visible error beats an endless refresh, so this
	// declines rather than recovering -- it is the rare configuration (site data
	// blocked) and the wrong side to fail on.
	if (!storage) return false

	let attempts = 0
	let lastAttempt = 0

	try {
		const raw = storage.getItem(RELOAD_STATE_KEY)

		if (raw) {
			const parsed = JSON.parse(raw)
			attempts = Number(parsed.attempts) || 0
			lastAttempt = Number(parsed.at) || 0
		}
	} catch (e) {
		attempts = 0
		lastAttempt = 0
	}

	if (lastAttempt && now - lastAttempt > ATTEMPT_RESET_MS) attempts = 0
	if (attempts >= MAX_ATTEMPTS) return false

	try {
		storage.setItem(RELOAD_STATE_KEY, JSON.stringify({ attempts: attempts + 1, at: now }))
	} catch (e) {
		// Could not record the attempt, so could not bound the next one.
		return false
	}

	promoteWaitingWorkerThenReload(win)

	return true
}

/**
 * Listen for the failure on both paths it can surface through: an uncaught error
 * (React re-throws a failed lazy import during render, which is what reaches
 * window.onerror) and an unhandled rejection (the dynamic import itself).
 *
 * Returns a teardown function.
 */
export const installStaleBuildRecovery = (win = window) => {
	const onError = event => {
		if (isStaleBuildError(event.error || event.message) || isHtmlServedAsScript(event)) {
			recoverFromStaleBuild(win)
		}
	}

	const onRejection = event => {
		if (isStaleBuildError(event.reason)) recoverFromStaleBuild(win)
	}

	/*
	 * Separate handler, and registered with CAPTURE, because a resource load
	 * failure does not bubble: an ordinary window listener is never called for
	 * it. That is the whole reason the 404 case went unnoticed.
	 */
	const onResourceError = event => {
		if (isOwnAssetThatFailedToLoad(event)) recoverFromStaleBuild(win)
	}

	win.addEventListener("error", onError)
	win.addEventListener("unhandledrejection", onRejection)
	win.addEventListener("error", onResourceError, true)

	return () => {
		win.removeEventListener("error", onError)
		win.removeEventListener("unhandledrejection", onRejection)
		win.removeEventListener("error", onResourceError, true)
	}
}

export default installStaleBuildRecovery
